import {
  IconAppCenter,
  IconHome,
  IconQuit,
  IconSetting,
  IconUserCircle,
  IconUserGroup,
} from '@douyinfe/semi-icons'
import {
  Button,
  Dropdown,
  Layout,
  Nav,
  Space,
  Tooltip,
  Typography,
} from '@douyinfe/semi-ui'
import { useQuery } from '@tanstack/react-query'
import { useEffect, useState } from 'react'
import { Outlet, useLocation, useNavigate } from 'react-router-dom'
import { api, isTokenExpired } from '@/api/client'
import { useAuthStore } from '@/stores/auth'
import { useProjectStore } from '@/stores/project'
import { ProjectMenu } from './ProjectMenu'

const { Header, Sider, Content } = Layout

export function AppLayout() {
  const navigate = useNavigate()
  const location = useLocation()
  const { user, token, logout } = useAuthStore()
  const { projects, setProjects, currentProject, setCurrentProject }
    = useProjectStore()
  const [collapsed, setCollapsed] = useState(false)

  useEffect(() => {
    if (!token)
      return navigate('/login', { replace: true })
    if (isTokenExpired(token)) {
      logout()
      navigate('/login', { replace: true })
    }
  }, [token, navigate, logout])

  const { data: projectsData } = useQuery({
    queryKey: ['projects'],
    queryFn: () => api.projects(),
    enabled: !!token,
  })

  useEffect(() => {
    if (projectsData?.data?.list) {
      setProjects(projectsData.data.list)
      if (!currentProject && projectsData.data.list.length > 0) {
        setCurrentProject(projectsData.data.list[0].code)
      }
    }
  }, [projectsData, setProjects, currentProject, setCurrentProject])

  const selectedKeys = [
    location.pathname === '/' ? 'dashboard' : location.pathname,
  ]

  const navItems = [
    {
      itemKey: 'dashboard',
      text: '首页',
      icon: <IconHome size="large" />,
      link: '/',
    },
    ...(user?.role === 'super_admin'
      ? [
          {
            itemKey: '/templates',
            text: '项目模板',
            icon: <IconAppCenter size="large" />,
            link: '/templates',
          },
          {
            itemKey: '/users',
            text: '用户管理',
            icon: <IconUserGroup size="large" />,
            link: '/users',
          },
        ]
      : []),
    {
      itemKey: '/wechat',
      text: '微信用户',
      icon: <IconUserCircle size="large" />,
      link: '/wechat',
    },
    {
      itemKey: '/settings',
      text: '系统设置',
      icon: <IconSetting size="large" />,
      link: '/settings',
    },
  ]

  return (
    <Layout style={{ minHeight: '100vh' }}>
      <Header
        style={{
          backgroundColor: 'var(--semi-color-bg-0)',
          borderBottom: '1px solid var(--semi-color-border)',
          position: 'sticky',
          top: 0,
          zIndex: 100,
          height: 56,
          lineHeight: '56px',
        }}
      >
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            height: '100%',
            padding: '0 20px',
          }}
        >
          <Space align="center" spacing={10}>
            <img
              src="/logo.png"
              alt="once.work"
              style={{
                width: 32,
                height: 32,
                borderRadius: 8,
                objectFit: 'contain',
              }}
            />
            <Typography.Title
              heading={6}
              style={{ margin: 0, fontWeight: 600 }}
            >
              授权运营中台
            </Typography.Title>
          </Space>
          <Space align="center" spacing={12}>
            <Tooltip content={user?.email || '当前用户'} position="bottomRight">
              <Dropdown
                position="bottomRight"
                render={(
                  <Dropdown.Menu>
                    <Dropdown.Item
                      icon={<IconQuit />}
                      type="danger"
                      onClick={logout}
                    >
                      退出登录
                    </Dropdown.Item>
                  </Dropdown.Menu>
                )}
              >
                <Button
                  icon={<IconUserCircle size="large" />}
                  theme="borderless"
                  aria-label="用户菜单"
                />
              </Dropdown>
            </Tooltip>
          </Space>
        </div>
      </Header>
      <Layout>
        <Sider
          style={{
            width: collapsed ? 60 : 240,
            transition: 'width 0.2s cubic-bezier(.62, .05, .36, .95)',
            overflow: 'hidden',
            flex: '0 0 auto',
            position: 'sticky',
            top: 56,
            height: 'calc(100vh - 56px)',
            alignSelf: 'flex-start',
          }}
        >
          <Nav
            isCollapsed={collapsed}
            onCollapseChange={setCollapsed}
            selectedKeys={selectedKeys}
            style={{ height: '100%' }}
            items={navItems}
            footer={{ collapseButton: true }}
          >
            <ProjectMenu projects={projects} collapsed={collapsed} />
          </Nav>
        </Sider>
        <Content
          style={{ padding: 24, backgroundColor: 'var(--semi-color-bg-0)' }}
        >
          <Outlet />
        </Content>
      </Layout>
    </Layout>
  )
}
